import {
  AfterContentInit,
  ChangeDetectionStrategy,
  Component,
  ContentChildren,
  Input,
  QueryList,
  signal,
} from '@angular/core';
import { CustomCodeComponent } from './custom-code.component';

/**
 * Usage:
 *
 *  <custom-code-tabs>
 *
 *    <custom-code [title]="'HTML code'" [code]="'htmlRaw'" [region]="''" [language]="'html'" [lineNums]="true">
 *      Loading...
 *    </custom-code>
 *
 *    <custom-code [title]="'TypeScript code'"  [code]="'tsRaw'" [region]="''" [language]="'ts'" [lineNums]="true">
 *      Loading...
 *    </custom-code>
 *
 *    <custom-code [title]="'CSS sample'" [language]="'css'" [lineNums]="false">
 *      Static content...
 *    </custom-code>
 *
 *   </custom-code-tabs>
 *
 *
 * We extended the custom-code component with a hidden property, this enabled us to manage visibility in a simple way.
 * A nice tab-control solution https://juristr.com/blog/2016/02/learning-ng2-creating-tab-component/
 *
 */
@Component({
  selector: 'custom-code-tabs',
  template: `
    <!-- title -->
    <div class="custom-code__header" [class.open]="showCode()">
      <div class="custom-code__header__left">
        <i class="fas fa-code mr-3"></i>
        @if (title) {
          <span>{{ title }}</span>
        }
        @if (canCollapse) {
          <span>
            <a href="javascript:void(0)" (click)="toggleCode()">
              @if (!showCode()) {
                Show code
              }
              @if (showCode()) {
                Hide code
              }
            </a>
          </span>
        }
      </div>
      <div class="custom-code__header__right">
        <!-- content selection -->
        @for (content of contents; track content; let i = $index) {
          <span
            class="custom-code__header__right_types"
            [class.active]="showCode() && i === showIndex()"
          >
            <a href="javascript:void(0)" (click)="refresh(i)">
              {{ content.title || content.language }}
            </a>
          </span>
        }
        @if (!hideCopy) {
          <span class="custom-code__header__copy">
            <a (click)="doCopy()"><i class="far fa-copy"></i></a>
          </span>
        }
      </div>
    </div>
    @if (showCode()) {
      <ng-content></ng-content>
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CustomCodeTabsComponent implements AfterContentInit {
  @Input() title: string;
  @Input() canCollapse = true;
  @Input() hideCopy = false;

  // Decorator query, not contentChildren(): the JIT spec build cannot register signal queries.
  @ContentChildren(CustomCodeComponent)
  contents: QueryList<CustomCodeComponent>;

  readonly showCode = signal(false);
  readonly showIndex = signal(0);

  // Content init runs before this view's first check, so an uncollapsible set renders open on the first pass.
  ngAfterContentInit(): void {
    if (this.canCollapse === false) this.showCode.set(true);
    this.refresh();
  }

  toggleCode() {
    this.showCode.update((show) => !show);
  }

  refresh(index?: number) {
    if (index !== undefined && index !== null) this.showIndex.set(index);

    this.contents.forEach((c, i) => {
      c.hidden.set(i !== this.showIndex());
    });
  }

  doCopy() {
    this.visibleContent()?.doCopy();
  }

  getContent() {
    return this.visibleContent()?.getContent();
  }

  private visibleContent(): CustomCodeComponent | undefined {
    return this.contents.find((c) => !c.hidden());
  }
}
